import { useEffect, useState } from 'react';
import { useAuth, ShellAuthProvider } from '@aimsos/auth';
import { AgentsApp } from '@aimsos/agents';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Agents application inside the Core Shell.
 * Mirrors the same embedding pattern as WorkspacesEmbed.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT — see
 * @aimsos/auth's useAuth.js) instead of useAuth0() directly, so this still
 * mounts correctly on a {slug}.{rootDomain} subdomain landed on via a
 * cross-subdomain handoff, where Auth0 SDK itself has no session.
 */
const AgentsEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/agents/i18n/register')
      .then(({ registerAgentsI18n }) => {
        registerAgentsI18n(registerNamespace);
        return i18n.loadNamespaces('agents');
      })
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch((err) => console.error('[AgentsEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = { mounted: true };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !i18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <AgentsApp />
    </ShellAuthProvider>
  );
};

export default AgentsEmbed;
